import "./csp-reporter-types";
/** Collects distinct CSP violations from one page-load and sends compact, cacheable GET batches. */

export interface CspReporterConfig {
	maxUrlLength?: number;
	debounceMs?: number;
}

export const DIRECTIVE_CODES: Record<string, string> = {
	'default-src': 'd', 'script-src': 's', 'script-src-elem': 'se', 'script-src-attr': 'sa',
	'style-src': 'y', 'style-src-elem': 'ye', 'style-src-attr': 'ya', 'img-src': 'i',
	'font-src': 'f', 'connect-src': 'c', 'media-src': 'm', 'object-src': 'o',
	'frame-src': 'r', 'frame-ancestors': 'ra', 'worker-src': 'w', 'manifest-src': 'n',
	'base-uri': 'b', 'form-action': 'x', 'require-trusted-types-for': 't',
	'upgrade-insecure-requests': 'u',
};

export const BLOCKED_URI_KEYWORDS: Record<string, string> = {
	inline: '!i', eval: '!e', data: '!d', blob: '!b', filesystem: '!f', self: '!s',
	'moz-extension': '!me', 'chrome-extension': '!ce', 'safari-web-extension': '!swe',
};

function encodeDirective(directive: string): string {
	return DIRECTIVE_CODES[directive] ?? `u:${directive}`;
}

const EXTENSION_SCHEME = /^(moz-extension|chrome-extension|safari-web-extension)(?::|$)/i;

function encodeBlockedUri(blockedUri: string): string {
	const extension = EXTENSION_SCHEME.exec(blockedUri)?.[1];
	if (extension) {
		return BLOCKED_URI_KEYWORDS[extension.toLowerCase()]!;
	}
	const keyword = BLOCKED_URI_KEYWORDS[blockedUri];
	if (keyword) {
		return keyword;
	}

	try {
		const url = new URL(blockedUri);
		const port = url.port ? `:${url.port}` : '';
		const scheme = url.protocol === 'https:' ? '' : `${url.protocol.replace(':', '')}+`;
		return `${scheme}${url.hostname}${port}`;
	} catch {
		return blockedUri.slice(0, 200);
	}
}

function normalizePolicy(policy: string|undefined): string {
	return policy?.split(';').map(part => part.trim().replaceAll(/\s+/g, ' ')).filter(Boolean).join(';').trim() ?? "";
}

let expectedPolicy: string|undefined;
function classifySource(event: SecurityPolicyViolationEvent): string {
	if (EXTENSION_SCHEME.test(event.blockedURI) || EXTENSION_SCHEME.test(event.sourceFile)) {
		return 'e';
	}
	expectedPolicy ??= normalizePolicy(document.querySelector<HTMLMetaElement>('meta[name="plaudit-csp"]')?.dataset.policy);
	if (!expectedPolicy || !event.originalPolicy?.trim()) {
		return 'u';
	}
	return expectedPolicy === normalizePolicy(event.originalPolicy) ? 's' : 'o';
}

export class CspReporter {
	private readonly maxUrlLength: number;
	private readonly debounceMs: number;
	private readonly seen = new Set<string>();
	private queue: string[] = [];
	private flushTimer?: ReturnType<typeof setTimeout>;

	constructor(config: CspReporterConfig = {}) {
		this.maxUrlLength = config.maxUrlLength ?? 1800;
		this.debounceMs = config.debounceMs ?? 500;
		const priorViolations = window.plauditSecurityPolicyViolation;
		window.plauditSecurityPolicyViolation = {push: this.record};
		if (Array.isArray(priorViolations)) {
			for (const priorViolation of priorViolations) {
				this.record(priorViolation);
			}
		}
		window.addEventListener('pagehide', () => this.flush());
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') this.flush();
		});
	}

	private endpoint() {
		return document.querySelector<HTMLMetaElement>('meta[name="plaudit-csp"]')?.content;
	}

	private readonly record = (event: SecurityPolicyViolationEvent): void => {
		if (EXTENSION_SCHEME.test(event.blockedURI) || EXTENSION_SCHEME.test(event.sourceFile)) {
			return;
		}
		if (this.isCollector(event.blockedURI)) {
			return;
		}

		const directive = event.effectiveDirective || event.violatedDirective;
		const token = `${encodeDirective(directive)},${encodeBlockedUri(event.blockedURI)},${classifySource(event)}`;
		if (this.seen.has(token)) {
			return;
		}

		this.seen.add(token);
		this.queue.push(token);
		if (this.flushTimer === undefined) {
			this.flushTimer = setTimeout(() => this.flush(), this.debounceMs);
		}
	}

	private isCollector(blockedUri: string): boolean {
		const endpoint = this.endpoint();
		if (!endpoint) {
			return false;
		}
		try {
			const blocked = new URL(blockedUri, location.href);
			const collector = new URL(endpoint, location.href);
			return blocked.origin === collector.origin && blocked.pathname === collector.pathname;
		} catch {
			return false;
		}
	}

	private flush(): void {
		if (this.flushTimer !== undefined) {
			clearTimeout(this.flushTimer);
			this.flushTimer = undefined;
		}
		const endpoint = this.endpoint();
		if (!endpoint || this.queue.length === 0) {
			return;
		}
		const queue = this.queue;
		this.queue = [];
		for (const batch of this.buildBatches(endpoint, queue)) {
			this.send(endpoint, batch);
		}
	}

	private base(endpoint: string): string {
		return `${endpoint}?p=${encodeURIComponent(location.pathname.slice(0, 800))}&v=`;
	}

	private buildBatches(endpoint: string, tokens: string[]): string[][] {
		const overhead = this.base(endpoint).length;
		const batches: string[][] = [];
		let current: string[] = [];
		let currentLength = overhead;
		for (const token of tokens) {
			const encodedLength = encodeURIComponent(token).length;
			if (overhead + encodedLength > this.maxUrlLength) continue;
			// The collector accepts at most 32 tokens per request.
			if (current.length >= 32 || (current.length > 0 && currentLength + 1 + encodedLength > this.maxUrlLength)) {
				batches.push(current);
				current = [];
				currentLength = overhead;
			}
			current.push(token);
			currentLength += encodedLength + (current.length > 1 ? 1 : 0);
		}
		if (current.length > 0) batches.push(current);
		return batches;
	}

	private send(endpoint: string, tokens: string[]): void {
		const url = this.base(endpoint) + tokens.map(encodeURIComponent).join('~');
		fetch(url, {method: 'GET', keepalive: true, credentials: 'omit'}).catch(() => {});
	}
}

new CspReporter();
